import { useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ChevronUp, Plane } from "lucide-react";

export default function BoardingPass({ onBoard }) {
  const y = useMotionValue(0);
  const opacity = useTransform(y, [-260, -120, 0], [0, 1, 1]);
  const scale = useTransform(y, [-260, 0], [0.96, 1]);
  const triggered = useRef(false);

  const handleDragEnd = (_e, info) => {
    if (triggered.current) return;
    if (info.offset.y < -120 || info.velocity.y < -400) {
      triggered.current = true;
      onBoard();
    }
  };

  return (
    <div
      className="relative w-full h-full overflow-hidden grain"
      style={{ background: "#0a0604" }}
      data-testid="boarding-pass-screen"
    >
      {/* Background video */}
      <video
        src="https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/730ffx4z_11043265-hd_1080_1920_30fps.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 0 }}
        data-testid="boarding-bg-video"
      />
      {/* Dark vignette overlay for readability */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 1,
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(10,6,4,0.25) 0%, rgba(10,6,4,0.55) 60%, rgba(10,6,4,0.85) 100%)",
        }}
      />
      {/* Floating gold particles */}
      {[...Array(18)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: 2 + (i % 3),
            height: 2 + (i % 3),
            background: "rgba(212, 175, 55, 0.6)",
            left: `${(i * 53) % 100}%`,
            top: `${(i * 37) % 100}%`,
            zIndex: 2,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.8, 0.2],
          }}
          transition={{
            duration: 4 + (i % 4),
            repeat: Infinity,
            delay: i * 0.2,
          }}
        />
      ))}

      {/* Emirates logo at top - cream backdrop badge */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center pt-5 pb-2 z-10">
        <div
          className="flex items-center justify-center rounded-2xl px-4 py-2"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(248,245,240,0.96) 0%, rgba(232,220,196,0.85) 70%, rgba(232,220,196,0.0) 100%)",
            boxShadow:
              "0 8px 30px rgba(212,175,55,0.35), 0 0 0 1px rgba(212,175,55,0.4)",
          }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_swipe-to-love/artifacts/q2sskn8j_image.png"
            alt="Emirates Birthday"
            className="h-24 w-auto object-contain"
            style={{
              mixBlendMode: "multiply",
              filter: "drop-shadow(0 2px 8px rgba(215,25,32,0.2))",
            }}
            data-testid="emirates-logo"
            draggable={false}
          />
        </div>
      </div>

      <div className="absolute top-36 left-0 right-0 text-center px-6 z-10">
        <div
          className="text-[#FFF7E0] text-xs tracking-[0.4em] uppercase font-body font-semibold"
          style={{ textShadow: "0 2px 10px rgba(0,0,0,0.85), 0 0 4px rgba(0,0,0,0.7)" }}
        >
          First Class · Private Suite
        </div>
        <div
          className="mt-2 text-[#F8F5F0] text-[11px] tracking-[0.3em] font-body font-medium"
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.85)" }}
        >
          Boarding Pass · 14 · Window
        </div>
      </div>

      {/* The draggable boarding pass card (positioning wrapper for true centering) */}
      <div
        className="absolute left-0 right-0 flex justify-center"
        style={{ bottom: "8%" }}
      >
      <motion.div
        style={{
          width: "min(360px, 86vw)",
          y,
          scale,
          opacity,
          touchAction: "none",
        }}
        drag="y"
        dragConstraints={{ top: -280, bottom: 0 }}
        dragElastic={0.25}
        onDragEnd={handleDragEnd}
        whileTap={{ cursor: "grabbing" }}
        data-testid="boarding-pass-card"
      >
        <div className="relative">
          {/* Top half */}
          <div className="leather-cream rounded-t-3xl px-6 pt-6 pb-5 shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_30px_rgba(255,215,100,0.25)] grain relative overflow-hidden" style={{ border: "1px solid rgba(255,231,160,0.55)" }}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[#3E2723]/60 text-[10px] tracking-[0.3em] uppercase">
                  Passenger
                </div>
                <div className="font-display text-[#1A110D] text-2xl mt-1 tracking-wide">
                  Ms. Farida
                </div>
              </div>
              <div className="text-right">
                <div className="text-[#3E2723]/60 text-[10px] tracking-[0.3em] uppercase">
                  Class
                </div>
                <div className="font-display text-[#D71920] text-xl mt-1 tracking-wide">
                  First
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <div>
                <div className="font-display text-3xl text-[#1A110D] leading-none">MAA</div>
                <div className="text-[10px] text-[#3E2723]/60 tracking-widest mt-1">CHENNAI</div>
              </div>
              <div className="flex-1 flex items-center px-2">
                <div className="h-[1px] flex-1 bg-[#3E2723]/30" />
                <Plane className="w-4 h-4 text-[#D4AF37] mx-2" />
                <div className="h-[1px] flex-1 bg-[#3E2723]/30" />
              </div>
              <div className="text-right">
                <div className="font-display text-3xl text-[#1A110D] leading-none">BDC</div>
                <div className="text-[10px] text-[#3E2723]/60 tracking-widest mt-1 whitespace-nowrap">BIRTHDAY CLOUDS</div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-4 text-[10px] tracking-widest">
              <div>
                <div className="text-[#3E2723]/60 uppercase">Flight</div>
                <div className="text-[#1A110D] font-medium mt-0.5">EK · 365</div>
              </div>
              <div>
                <div className="text-[#3E2723]/60 uppercase">Date</div>
                <div className="text-[#1A110D] font-medium mt-0.5">Today</div>
              </div>
              <div>
                <div className="text-[#3E2723]/60 uppercase">Suite</div>
                <div className="text-[#1A110D] font-medium mt-0.5">1A · Window</div>
              </div>
            </div>
          </div>

          {/* Perforation */}
          <div className="ticket-cut h-3" style={{ background: "#FFF1C9" }} />

          {/* Bottom half */}
          <div className="leather-cream rounded-b-3xl px-6 py-5 shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_30px_rgba(255,215,100,0.25)] grain relative" style={{ borderTop: "none", border: "1px solid rgba(255,231,160,0.55)" }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[#3E2723]/60 text-[10px] tracking-[0.3em] uppercase">
                  Gate
                </div>
                <div className="font-display text-[#1A110D] text-lg">A · 7</div>
              </div>
              <div>
                <div className="text-[#3E2723]/60 text-[10px] tracking-[0.3em] uppercase">
                  Seq
                </div>
                <div className="font-display text-[#1A110D] text-lg">023</div>
              </div>
              <div className="flex flex-col items-end gap-[2px]">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((b) => (
                  <div
                    key={b}
                    className="bg-[#1A110D]"
                    style={{
                      width: 60,
                      height: 2,
                      opacity: b % 2 === 0 ? 1 : 0.5,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Swipe up affordance overlay */}
          <div className="absolute -top-14 left-0 right-0 flex flex-col items-center pointer-events-none">
            <ChevronUp className="float-arrow w-7 h-7 text-[#FFE7A0]" strokeWidth={2} />
            <div
              className="text-[#FFF7E0] text-[12px] tracking-[0.4em] uppercase mt-1 font-body font-bold whitespace-nowrap"
              style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)" }}
            >
              Swipe up to board
            </div>
          </div>
        </div>
      </motion.div>
      </div>

      {/* Bottom hint */}
      <div
        className="absolute bottom-3 left-0 right-0 text-center text-[#F8F5F0] text-[11px] tracking-[0.3em] font-body font-semibold z-10"
        style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9)" }}
      >
        · A LUXURY BIRTHDAY EXPERIENCE ·
      </div>
    </div>
  );
}
